import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Armchair, RefreshCw, Users2 } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import {
  AreaChart,
  BarChart,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DonutChart,
  DonutLegend,
} from "@eduviaos/ui-kit";
import { userHasPermission, type UserRole } from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";
import { api } from "@/lib/api";
import { useAdminStudyGroups } from "@/lib/learning/adminApi";
import { useStudentsLearningSummary } from "@/hooks/useStudentsLearning";
import {
  CHART_SERIES_COLOR,
  SUMMARY_PAGE_LIMIT,
  TREND_DAYS,
  learnerActivity,
  seatingFloorFromStats,
  studyGroupBars,
  trendIsEmpty,
  type BookingDashboardStats,
  type OccupancySegment,
  type ShiftBar,
  type StudyGroupBar,
  type TrendPoint,
  type LearnerActivityRow,
} from "@/lib/dashboard/studyCentreChartModel";

const EASE_OUT = [0.23, 1, 0.32, 1] as const;

/**
 * `moderate_study_groups` is granted to admin on the backend (SC-21e) but
 * is not in the shared FE permission catalog yet. Admin, owner, and
 * super-admin match that grant; other roles only pass when the string is
 * present on their overrides.
 */
function canModerateStudyGroups(role: UserRole, overrides: readonly string[]): boolean {
  if (role === "admin" || role === "owner" || role === "super_admin") return true;
  return overrides.includes("moderate_study_groups");
}

type ChartStatus = "loading" | "error" | "empty" | "ready";

async function loadSeatingFloor() {
  const stats = await api.get<BookingDashboardStats>(
    `/api/bookings/dashboard-stats?days=${TREND_DAYS}`,
  );
  return seatingFloorFromStats(stats);
}

export function StudyCentreCharts({
  enabled,
  learningEnabled,
}: {
  enabled: boolean;
  learningEnabled: boolean;
}) {
  const reduce = useReducedMotion();
  const { user } = useAuth();
  const canBook = Boolean(
    user &&
    userHasPermission(
      user.role,
      user.permission_overrides ?? [],
      "manage_bookings",
      user.permission_mode ?? "additive",
    ),
  );
  const canLearners = Boolean(
    user &&
    userHasPermission(
      user.role,
      user.permission_overrides ?? [],
      "view_students",
      user.permission_mode ?? "additive",
    ),
  );
  const canGroups = Boolean(
    user && canModerateStudyGroups(user.role, user.permission_overrides ?? []),
  );

  const seating = useQuery({
    queryKey: ["ia", "dashboard", "sc-seating"],
    queryFn: loadSeatingFloor,
    enabled: enabled && canBook,
    staleTime: 30_000,
  });

  const learners = useStudentsLearningSummary(
    { status: "active", limit: SUMMARY_PAGE_LIMIT },
    enabled && learningEnabled && canLearners,
  );
  const groups = useAdminStudyGroups({ limit: 100 }, enabled && learningEnabled && canGroups);

  if (!enabled) return null;
  const showSeating = canBook;
  const showLearning = learningEnabled && (canLearners || canGroups);
  if (!showSeating && !showLearning) return null;

  const learnerRows = learnerActivity(learners.data?.items ?? []);
  const learnerTotal = learners.data?.total ?? 0;
  const learnerPage = learners.data?.items.length ?? 0;
  const groupRows = studyGroupBars(groups.data?.items ?? []);

  return (
    <motion.section
      aria-label="Study centre operations"
      className="grid gap-3 lg:grid-cols-2"
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: EASE_OUT }}
    >
      {showSeating ? (
        <>
          <SeatOccupancyChart
            status={chartStatus(
              seating.isPending,
              seating.isError,
              (seating.data?.occupancy.length ?? 0) === 0,
            )}
            segments={seating.data?.occupancy ?? []}
            summary={seating.data?.occupancySummary ?? ""}
            caption={seating.data?.occupancyCaption ?? null}
            onRetry={() => void seating.refetch()}
          />
          <BookingsByShiftChart
            status={chartStatus(
              seating.isPending,
              seating.isError,
              (seating.data?.shifts.length ?? 0) === 0,
            )}
            rows={seating.data?.shifts ?? []}
            onRetry={() => void seating.refetch()}
          />
          <div className="lg:col-span-2">
            <BookingsTrendChart
              status={chartStatus(
                seating.isPending,
                seating.isError,
                seating.data ? trendIsEmpty(seating.data.trend) : true,
              )}
              points={seating.data?.trend ?? []}
              onRetry={() => void seating.refetch()}
            />
          </div>
        </>
      ) : null}
      {learningEnabled && canLearners ? (
        <LearnerActivityChart
          status={chartStatus(learners.isPending, learners.isError, learnerTotal === 0)}
          rows={learnerRows}
          total={learnerTotal}
          shown={learnerPage}
          onRetry={() => void learners.refetch()}
        />
      ) : null}
      {learningEnabled && canGroups ? (
        <StudyGroupActivityChart
          status={chartStatus(groups.isPending, groups.isError, (groups.data?.total ?? 0) === 0)}
          rows={groupRows}
          total={groups.data?.total ?? 0}
          shown={groups.data?.items.length ?? 0}
          onRetry={() => void groups.refetch()}
        />
      ) : null}
    </motion.section>
  );
}

function chartStatus(pending: boolean, error: boolean, empty: boolean): ChartStatus {
  if (pending) return "loading";
  if (error) return "error";
  if (empty) return "empty";
  return "ready";
}

function ChartSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading chart" className="grid h-52 gap-3">
      <div className="bg-muted h-3 w-24 animate-pulse rounded motion-reduce:animate-none" />
      <div className="bg-muted h-40 animate-pulse rounded-lg motion-reduce:animate-none" />
    </div>
  );
}

function ChartEmpty({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex h-52 flex-col items-center justify-center gap-2 text-center">
      <span
        aria-hidden="true"
        className="bg-muted text-muted-foreground grid size-10 place-items-center rounded-full"
      >
        <Armchair className="size-4" />
      </span>
      <p className="text-sm font-medium">{title}</p>
      <p className="text-muted-foreground max-w-xs text-xs">{body}</p>
    </div>
  );
}

function ChartError({ onRetry }: { onRetry: () => void }) {
  return (
    <div role="alert" className="flex h-52 flex-col items-center justify-center gap-3 text-center">
      <p className="text-sm font-medium">Couldn't load this chart.</p>
      <p className="text-muted-foreground max-w-xs text-xs">
        The request failed. Your other dashboard numbers are unchanged.
      </p>
      <Button type="button" variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="size-4" aria-hidden="true" />
        Retry
      </Button>
    </div>
  );
}

function ChartFrame({
  title,
  description,
  status,
  onRetry,
  emptyTitle,
  emptyBody,
  summary,
  footer,
  children,
}: {
  title: string;
  description: string;
  status: ChartStatus;
  onRetry: () => void;
  emptyTitle: string;
  emptyBody: string;
  summary: string;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {status === "loading" ? <ChartSkeleton /> : null}
        {status === "error" ? <ChartError onRetry={onRetry} /> : null}
        {status === "empty" ? <ChartEmpty title={emptyTitle} body={emptyBody} /> : null}
        {status === "ready" ? (
          <div role="img" aria-label={summary} className="grid gap-3">
            {children}
          </div>
        ) : null}
        {footer}
      </CardContent>
    </Card>
  );
}

export function SeatOccupancyChart({
  status,
  segments,
  summary,
  caption,
  onRetry,
}: {
  status: ChartStatus;
  segments: OccupancySegment[];
  summary: string;
  caption: string | null;
  onRetry: () => void;
}) {
  return (
    <ChartFrame
      title="Seat occupancy today"
      description="Distinct seats booked, checked in, or still free today."
      status={status}
      onRetry={onRetry}
      emptyTitle="No seat activity today"
      emptyBody="Bookings and active seats will draw this chart once the floor has either."
      summary={summary}
      footer={
        <Button asChild variant="ghost" size="sm">
          <Link to="/seating/bookings">Open bookings</Link>
        </Button>
      }
    >
      <div className="grid items-center gap-4 sm:grid-cols-[minmax(0,1fr)_9rem]">
        <DonutChart segments={segments} centerLabel="Today" height={200} />
        <DonutLegend segments={segments} />
      </div>
      {caption ? <p className="text-muted-foreground text-xs">{caption}</p> : null}
    </ChartFrame>
  );
}

export function BookingsByShiftChart({
  status,
  rows,
  onRetry,
}: {
  status: ChartStatus;
  rows: ShiftBar[];
  onRetry: () => void;
}) {
  const summary = rows.map((row) => `${row.shift} ${row.bookings}`).join(", ");
  return (
    <ChartFrame
      title="Bookings by shift"
      description="Today's bookings that were not cancelled, grouped by shift."
      status={status}
      onRetry={onRetry}
      emptyTitle="No bookings by shift"
      emptyBody="When learners book a morning, afternoon, or custom shift, it lands here."
      summary={summary || "No bookings by shift"}
    >
      <div className="h-52">
        <BarChart
          allowDecimals={false}
          data={rows}
          xKey="shift"
          series={[{ dataKey: "bookings", name: "Bookings", color: CHART_SERIES_COLOR }]}
          height={208}
        />
      </div>
    </ChartFrame>
  );
}

export function BookingsTrendChart({
  status,
  points,
  onRetry,
}: {
  status: ChartStatus;
  points: TrendPoint[];
  onRetry: () => void;
}) {
  const total = points.reduce((sum, point) => sum + point.bookings, 0);
  return (
    <ChartFrame
      title="Bookings, last 14 days"
      description="Every booking recorded on each day, all statuses included."
      status={status}
      onRetry={onRetry}
      emptyTitle="No bookings in the last 14 days"
      emptyBody="A quiet fortnight. New bookings will draw the trend as they are created."
      summary={`14-day bookings, ${total} total`}
    >
      <div className="h-52">
        <AreaChart
          allowDecimals={false}
          data={points}
          xKey="day"
          series={[{ dataKey: "bookings", name: "Bookings", color: CHART_SERIES_COLOR }]}
          height={208}
        />
      </div>
    </ChartFrame>
  );
}

export function LearnerActivityChart({
  status,
  rows,
  total,
  shown,
  onRetry,
}: {
  status: ChartStatus;
  rows: LearnerActivityRow[];
  total: number;
  shown: number;
  onRetry: () => void;
}) {
  const summary = rows.map((row) => `${row.metric} ${row.learners}`).join(", ");
  return (
    <ChartFrame
      title="Learner activity"
      description="Active learners with a plan, cards due, or a study streak."
      status={status}
      onRetry={onRetry}
      emptyTitle="No active learners yet"
      emptyBody="Invite learners and they'll appear here once a plan or review card exists."
      summary={summary || "No learner activity"}
      footer={
        <Button asChild variant="ghost" size="sm">
          <Link to="/learning">Open learning</Link>
        </Button>
      }
    >
      <div className="h-52">
        <BarChart
          allowDecimals={false}
          data={rows}
          xKey="metric"
          series={[{ dataKey: "learners", name: "Learners", color: CHART_SERIES_COLOR }]}
          height={208}
        />
      </div>
      <p className="text-muted-foreground text-xs tabular-nums">
        {shown < total
          ? `Based on the first ${shown} of ${total} active learners.`
          : `Across ${total} active learner${total === 1 ? "" : "s"}.`}
      </p>
    </ChartFrame>
  );
}

export function StudyGroupActivityChart({
  status,
  rows,
  total,
  shown,
  onRetry,
}: {
  status: ChartStatus;
  rows: StudyGroupBar[];
  total: number;
  shown: number;
  onRetry: () => void;
}) {
  const members = rows.reduce((sum, row) => sum + row.members, 0);
  return (
    <ChartFrame
      title="Study groups"
      description="Members in each group your centre can moderate."
      status={status}
      onRetry={onRetry}
      emptyTitle="No study groups yet"
      emptyBody="Groups learners create in the portal show up here with their member counts."
      summary={`${total} study groups, ${members} members in view`}
      footer={
        <Button asChild variant="ghost" size="sm">
          <Link to="/learning">Moderate groups</Link>
        </Button>
      }
    >
      <div className="flex items-center gap-2 text-xs">
        <Users2 className="text-muted-foreground size-4" aria-hidden="true" />
        <span className="text-muted-foreground tabular-nums">{members} members in view</span>
      </div>
      <div className="h-52">
        <BarChart
          allowDecimals={false}
          data={rows}
          xKey="group"
          series={[{ dataKey: "members", name: "Members", color: CHART_SERIES_COLOR }]}
          height={208}
        />
      </div>
      {shown < total ? (
        <p className="text-muted-foreground text-xs tabular-nums">
          Showing {shown} of {total} groups.
        </p>
      ) : null}
    </ChartFrame>
  );
}
